import React, { useState, useEffect, useCallback, ReactElement, EffectCallback } from 'react';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import {
  AccountabilityTemplate,
  Amount,
  CharityActivity,
  CharityTemplate,
  Condition,
  Measurement,
  MeasurementDiagram,
  MemberScore,
  OptionalExercise,
  RecommendedExercise,
  Resource,
  SelfCareActivity,
  Session,
  SessionTemplate,
  TemplateExercise,
  Weighting
} from '../../../types/entity.types';
import { Document } from '../../../types/document.types';
import { RootState } from '../../../types/state/root.types';
import { PlanState } from '../../../types/state/plan.types';
import { AccountabilityState } from '../../../types/state/accountablity.types';
import { ProgrammeState } from '../../../types/state/programme.types';

import { downloadFile, generateUUID, downloadExcelFile } from '../../../utils/util';
import {
  initialSelfCareActivities,
  initialConditions,
  initialRecommendedExercises,
  initialOptionalExercises,
  initialCharityActivities
} from '../../../utils/accountability-util';
import { ERROR, MEMBERS, TEAMS, UPCOMING } from '../../../utils/constants';

import {
  requestCreateAccountabilityTemplate,
  requestGenerateAccountabilityTemplateSessions,
  requestUpdateAccountabilityTemplate,
  requestViewAccountabilityTemplate
} from '../../../api/accountability-template/accountability-template.actions';
import { requestViewAllCollections } from '../../../api/collection/collection.actions';
import {
  requestCreateResource,
  requestUpdateResource,
  requestDeleteResource,
  requestViewResourcesByType
} from '../../../api/resource/resource.actions';
import { requestDownloadTeamLeaderboardExcel, requestViewProgramme } from '../../../api/programme/programme.actions';
import { requestDownloadLeaderboardExcel, requestViewLeaderboard } from '../../../api/leaderboard/leaderboard.actions';
import { requestDownloadDocument, requestUploadDocument } from '../../../api/document/document.actions';
import { requestViewTeamLeaderboard } from '../../../api/programme-teams/programme-teams.actions';

import { setLeaderboard, setTeamLeaderboard } from '../../../slices/programme/accountability/accountability.slice';
import { addSystemNotice } from '../../../slices/system/system.slice';

import Accountability from '../../../components/screens/programme/accountability/Accountability';

const AccountabilityContainer = (): ReactElement => {
  const [weightings, setWeightings] = useState<Weighting>({});
  const [measurement, setMeasurement] = useState<Measurement>({});
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [recommendedExercises, setRecommendedExercises] = useState<RecommendedExercise[]>(initialRecommendedExercises);
  const [optionalExercises, setOptionalExercises] = useState<OptionalExercise[]>(initialOptionalExercises);
  const [charityActivities, setCharityActivities] = useState<CharityActivity[]>(initialCharityActivities);
  const [selfCareActivities, setSelfCareActivities] = useState<SelfCareActivity[]>(initialSelfCareActivities);
  const [sessions, setSessions] = useState<Session[]>([]);

  const { accountabilityTemplate, leaderboard, teamLeaderboard } = useSelector(
    ({ accountability }: RootState): AccountabilityState => accountability
  );
  const { collections, resources } = useSelector(({ plan }: RootState): PlanState => plan);
  const { programme } = useSelector(({ programme }: RootState): ProgrammeState => programme);

  const dispatch: Dispatch = useDispatch();

  const navigate: NavigateFunction = useNavigate();

  const { status, programmeExternalReference, view }: Params = useParams();

  const retrieveMemberLeaderboard: () => void = useCallback((): void => {
    // @ts-expect-error TODO: Fix after adding types to leaderboard actions
    dispatch(requestViewLeaderboard({ externalReference: programmeExternalReference }));
  }, [dispatch, programmeExternalReference]);

  const retrieveTeamLeaderboard: () => void = useCallback((): void => {
    // @ts-expect-error TODO: Fix after adding types to programme team actions
    dispatch(requestViewTeamLeaderboard({ externalReference: programmeExternalReference }));
  }, [dispatch, programmeExternalReference]);

  useEffect((): void => {
    if (programmeExternalReference) {
      // @ts-expect-error TODO: Fix after adding types to programme actions
      dispatch(requestViewProgramme({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to accountability template actions
      dispatch(requestViewAccountabilityTemplate({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to collection actions
      dispatch(requestViewAllCollections({ externalReference: programmeExternalReference }));
      // @ts-expect-error TODO: Fix after adding types to resource actions
      dispatch(requestViewResourcesByType({ programmeExternalReference, type: 'meal-plan' }));
    }
  }, [dispatch, programmeExternalReference, status]);

  useEffect((): void => {
    if (status === UPCOMING) return;

    if (view === MEMBERS && !leaderboard) {
      retrieveMemberLeaderboard();
    } else if (view === TEAMS && !teamLeaderboard) {
      retrieveTeamLeaderboard();
    }
  }, [dispatch, leaderboard, retrieveMemberLeaderboard, retrieveTeamLeaderboard, status, teamLeaderboard, view]);

  useEffect((): ReturnType<EffectCallback> => {
    return (): void => {
      dispatch(setLeaderboard(undefined));
      dispatch(setTeamLeaderboard(undefined));
    };
  }, [dispatch]);

  useEffect((): void => {
    if (accountabilityTemplate) {
      const {
        measurementTemplate,
        measurement,
        conditions,
        recommendedExercise,
        optionalExercise,
        charityTemplates,
        selfCare,
        sessions
      } = accountabilityTemplate;

      setWeightings(measurementTemplate);
      setMeasurement(measurement);
      setSessions(sessions);

      if (conditions?.length > 0) {
        const mappedConditions: Condition[] = conditions.map(
          (condition: Condition): Condition => ({
            ...condition,
            externalReference: generateUUID()
          })
        );
        setConditions(mappedConditions);
      }

      if (recommendedExercise?.length > 0) {
        const exerciseMap: Map<string, RecommendedExercise> = new Map();

        recommendedExercise.forEach((exercise: TemplateExercise): void => {
          const { name } = exercise;
          const amount: Amount = {
            externalReference: exercise.externalReference,
            fromWeek: exercise.fromWeek,
            toWeek: exercise.toWeek,
            amount: exercise.dailyAmount
          };

          const exerciseInMap: RecommendedExercise | undefined = exerciseMap.get(name);
          if (exerciseInMap) {
            exerciseInMap.amounts.push(amount);
          } else {
            exerciseMap.set(name, {
              externalReference: generateUUID(),
              name,
              measurement: exercise.measurement,
              amounts: [amount]
            });
          }
        });

        setRecommendedExercises(Array.from(exerciseMap.values()));
      }

      if (optionalExercise?.length > 0) {
        setOptionalExercises(optionalExercise);
      }

      if (charityTemplates?.length > 0) {
        const charityMap: Map<string, CharityActivity> = new Map();

        charityTemplates.forEach((charityTemplate: CharityTemplate): void => {
          const { name } = charityTemplate;
          const amount: Amount = {
            externalReference: charityTemplate.externalReference,
            fromWeek: charityTemplate.fromWeek,
            toWeek: charityTemplate.toWeek,
            amount: charityTemplate.dailyAmount
          };

          const charityTemplateInMap: CharityActivity | undefined = charityMap.get(name);
          if (charityTemplateInMap) {
            charityTemplateInMap.amounts.push(amount);
          } else {
            charityMap.set(name, {
              externalReference: generateUUID(),
              name,
              measurement: charityTemplate.measurement,
              amounts: [amount]
            });
          }
        });

        setCharityActivities(Array.from(charityMap.values()));
      }

      if (selfCare?.length > 0) {
        setSelfCareActivities(
          selfCare.filter((activity: SelfCareActivity): boolean => activity.name.toLowerCase() !== 'sleep')
        );
      }
    }
  }, [accountabilityTemplate]);

  const updateWeighting = (field: keyof Weighting, value: number): void => {
    const copyOfWeightings: Weighting = structuredClone(weightings);
    copyOfWeightings[field] = value;
    setWeightings(copyOfWeightings);
  };

  const updateMeasurement = (field: keyof Measurement, value: MeasurementDiagram | string | undefined): void => {
    const copyOfMeasurement: Measurement = structuredClone(measurement);

    if (field === 'measurementDiagram') {
      copyOfMeasurement[field] = value as MeasurementDiagram;
    } else {
      copyOfMeasurement[field] = value as string;
    }

    setMeasurement(copyOfMeasurement);
  };

  const uploadDocument = async (document: Document): Promise<string> => {
    return new Promise((resolve: (value: string | PromiseLike<string>) => void): void => {
      dispatch(
        // @ts-expect-error TODO: Fix after adding types to document actions
        requestUploadDocument({
          document,
          onSuccess: (documentExternalReference: string): void => {
            resolve(documentExternalReference);
          }
        })
      );
    });
  };

  const createMealPlan = async (collectionExternalReference: string, resource: Resource): Promise<void> => {
    const documentExternalReference: string = await uploadDocument(resource.document as Document);
    delete resource.document;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestCreateResource({
        input: {
          collectionExternalReference,
          resource: {
            ...resource,
            documentExternalReference
          },
          programmeExternalReference
        },
        programmeExternalReference,
        // @ts-expect-error TODO: Fix after adding types to resource actions
        onSuccess: (): void => dispatch(requestViewResourcesByType({ programmeExternalReference, type: 'meal-plan' }))
      })
    );
  };

  const updateMealPlan = async (resource: Resource): Promise<void> => {
    if (resource.document) {
      const documentExternalReference: string = await uploadDocument(resource.document);
      resource.documentExternalReference = documentExternalReference;
      delete resource.document;
    }

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestUpdateResource({
        input: {
          collectionExternalReference: resource.collectionExternalReference,
          resource,
          programmeExternalReference
        },
        programmeExternalReference,
        // @ts-expect-error TODO: Fix after adding types to resource actions
        onSuccess: (): void => dispatch(requestViewResourcesByType({ programmeExternalReference, type: 'meal-plan' }))
      })
    );
  };

  const deleteMealPlan = (collectionExternalReference: string, resourceExternalReference: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to resource actions
      requestDeleteResource({
        programmeExternalReference,
        collectionExternalReference,
        resourceExternalReference,
        // @ts-expect-error TODO: Fix after adding types to resource actions
        onSuccess: (): void => dispatch(requestViewResourcesByType({ programmeExternalReference, type: 'meal-plan' }))
      })
    );
  };

  const createCondition = (): void => {
    const condition: Condition = {
      externalReference: generateUUID(),
      optionOne: '',
      optionTwo: ''
    };

    setConditions((prevState: Condition[]): Condition[] => [...prevState, condition]);
  };

  const updateCondition = (externalReference: string, field: keyof Condition, value: string): void => {
    const copyOfConditions: Condition[] = structuredClone(conditions);
    copyOfConditions.forEach((condition: Condition): void => {
      if (condition.externalReference === externalReference) {
        condition[field] = value;
      }
    });

    setConditions(copyOfConditions);
  };

  const deleteCondition = (externalReference: string): void => {
    setConditions(
      conditions.filter((condition: Condition): boolean => condition.externalReference !== externalReference)
    );
  };

  const createRecommendedExercise = (): void => {
    const exercise: RecommendedExercise = {
      externalReference: generateUUID(),
      name: '',
      measurement: '',
      amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
    };

    setRecommendedExercises((prevState: RecommendedExercise[]): RecommendedExercise[] => [...prevState, exercise]);
  };

  const updateRecommendedExercise = (
    externalReference: string,
    field: keyof Omit<RecommendedExercise, 'amounts'>,
    value: string
  ): void => {
    const copyOfRecommendedExercises: RecommendedExercise[] = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise: RecommendedExercise): void => {
      if (exercise.externalReference === externalReference) {
        exercise[field] = value;
      }
    });

    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const deleteRecommendedExercise = (externalReference: string): void => {
    setRecommendedExercises(
      recommendedExercises.filter(
        (exercise: RecommendedExercise): boolean => exercise.externalReference !== externalReference
      )
    );
  };

  const createExerciseAmount = (externalReference: string): void => {
    const copyOfRecommendedExercises: RecommendedExercise[] = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise: RecommendedExercise): void => {
      if (exercise.externalReference === externalReference) {
        const amount: Amount = { externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' };
        exercise.amounts.push(amount);
      }
    });

    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const updateExerciseAmount = (
    exerciseExternalReference: string,
    amountExternalReference: string,
    field: keyof Amount,
    value: string
  ): void => {
    const copyOfRecommendedExercises: RecommendedExercise[] = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise: RecommendedExercise): void => {
      if (exercise.externalReference === exerciseExternalReference) {
        exercise.amounts.forEach((amount: Amount): void => {
          if (amount.externalReference === amountExternalReference) {
            amount[field] = value;
          }
        });
      }
    });
    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const deleteExerciseAmount = (exerciseExternalReference: string, amountExternalReference: string): void => {
    const copyOfRecommendedExercises: RecommendedExercise[] = structuredClone(recommendedExercises);
    copyOfRecommendedExercises.forEach((exercise: RecommendedExercise): void => {
      if (exercise.externalReference === exerciseExternalReference) {
        exercise.amounts = exercise.amounts.filter(
          (amount: Amount): boolean => amount.externalReference !== amountExternalReference
        );
      }
    });
    setRecommendedExercises(copyOfRecommendedExercises);
  };

  const createOptionalExercise = (): void => {
    const exercise: OptionalExercise = {
      externalReference: generateUUID(),
      name: '',
      measurement: ''
    };

    setOptionalExercises((prevState: OptionalExercise[]): OptionalExercise[] => [...prevState, exercise]);
  };

  const updateOptionalExercise = (externalReference: string, field: keyof OptionalExercise, value: string): void => {
    const copyOfOptionalExercises: OptionalExercise[] = structuredClone(optionalExercises);
    copyOfOptionalExercises.forEach((exercise: OptionalExercise): void => {
      if (exercise.externalReference === externalReference) {
        exercise[field] = value;
      }
    });

    setOptionalExercises(copyOfOptionalExercises);
  };

  const deleteOptionalExercise = (externalReference: string): void => {
    setOptionalExercises(
      optionalExercises.filter(
        (exercise: OptionalExercise): boolean => exercise.externalReference !== externalReference
      )
    );
  };

  const createCharityActivity = (): void => {
    const charityActivity: CharityActivity = {
      externalReference: generateUUID(),
      name: '',
      measurement: '',
      amounts: [{ externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' }]
    };

    setCharityActivities((prevState: CharityActivity[]): CharityActivity[] => [...prevState, charityActivity]);
  };

  const updateCharityActivity = (
    externalReference: string,
    field: keyof Omit<CharityActivity, 'amounts'>,
    value: string
  ): void => {
    const copyOfCharityActivities: CharityActivity[] = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity: CharityActivity): void => {
      if (charityActivity.externalReference === externalReference) {
        charityActivity[field] = value;
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const deleteCharityActivity = (externalReference: string): void => {
    setCharityActivities(
      charityActivities.filter(
        (charityActivity: CharityActivity): boolean => charityActivity.externalReference !== externalReference
      )
    );
  };

  const createCharityAmount = (externalReference: string): void => {
    const copyOfCharityExercises: CharityActivity[] = structuredClone(charityActivities);
    copyOfCharityExercises.forEach((charityActivity: CharityActivity): void => {
      if (charityActivity.externalReference === externalReference) {
        const amount: Amount = { externalReference: generateUUID(), fromWeek: '', toWeek: '', amount: '' };
        charityActivity.amounts.push(amount);
      }
    });

    setCharityActivities(copyOfCharityExercises);
  };

  const updateCharityAmount = (
    charityActivityExternalReference: string,
    amountExternalReference: string,
    field: keyof Amount,
    value: string
  ): void => {
    const copyOfCharityActivities: CharityActivity[] = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity: CharityActivity): void => {
      if (charityActivity.externalReference === charityActivityExternalReference) {
        charityActivity.amounts.forEach((amount: Amount): void => {
          if (amount.externalReference === amountExternalReference) {
            amount[field] = value;
          }
        });
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const deleteCharityAmount = (charityActivityExternalReference: string, amountExternalReference: string): void => {
    const copyOfCharityActivities: CharityActivity[] = structuredClone(charityActivities);
    copyOfCharityActivities.forEach((charityActivity: CharityActivity): void => {
      if (charityActivity.externalReference === charityActivityExternalReference) {
        charityActivity.amounts = charityActivity.amounts.filter(
          (amount: Amount): boolean => amount.externalReference !== amountExternalReference
        );
      }
    });

    setCharityActivities(copyOfCharityActivities);
  };

  const createSelfCareActivity = (): void => {
    const activity: SelfCareActivity = {
      externalReference: generateUUID(),
      name: ''
    };

    setSelfCareActivities((prevState: SelfCareActivity[]): SelfCareActivity[] => [...prevState, activity]);
  };

  const updateSelfCareActivity = (externalReference: string, value: string): void => {
    const copyOfSelfCareActivities: SelfCareActivity[] = structuredClone(selfCareActivities);
    copyOfSelfCareActivities.forEach((activity: SelfCareActivity): void => {
      if (activity.externalReference === externalReference) {
        activity.name = value;
      }
    });

    setSelfCareActivities(copyOfSelfCareActivities);
  };

  const deleteSelfCareActivity = (externalReference: string): void => {
    setSelfCareActivities(
      selfCareActivities.filter(
        (activity: SelfCareActivity): boolean => activity.externalReference !== externalReference
      )
    );
  };

  const updateSession = (updatedSession: Partial<Session>): void => {
    const copyOfSessions: Session[] = structuredClone(sessions);
    copyOfSessions.forEach((session: Session): void => {
      if (session.externalReference === updatedSession.externalReference) {
        session.name = updatedSession.name as string;
        session.startTime = updatedSession.startTime as Date;
        session.endTime = updatedSession.endTime as Date;
        session.date = updatedSession.date;
      }
    });

    setSessions(copyOfSessions);
  };

  const deleteSession = (externalReference: string): void => {
    setSessions(sessions.filter((session: Session): boolean => session.externalReference !== externalReference));
  };

  const validateMeasurements = (): boolean => {
    if (!measurement.measurementFrequency || !measurement.measurementStartDay) {
      dispatch(addSystemNotice({ type: ERROR, message: 'Measurements section incomplete' }));
      return false;
    }

    return true;
  };

  const validateWeightings = (): boolean => {
    let total: number = 0;

    total += Number(weightings.weight) || 0;
    total += Number(weightings.cm) || 0;
    total += Number(weightings.steps) || 0;
    total += Number(weightings.objectives) || 0;

    if (programme.charityWork) {
      total += Number(weightings.charity) || 0;
    }

    if (total !== 100) {
      dispatch(addSystemNotice({ type: ERROR, message: 'Weightings do not add up to 100%' }));
      return false;
    }

    return true;
  };

  const saveAccountabilityTemplate = (redirectUrl?: string): void => {
    const measurementsValid: boolean = validateMeasurements();
    const weightingsValid: boolean = validateWeightings();

    if (!measurementsValid || !weightingsValid) return;

    const mappedRecommendedExercises: TemplateExercise[] = [];
    recommendedExercises.forEach((exercise: RecommendedExercise): void => {
      exercise.amounts.forEach((amount: Amount): void => {
        mappedRecommendedExercises.push({
          externalReference: amount.externalReference,
          name: exercise.name,
          dailyAmount: amount.amount,
          measurement: exercise.measurement,
          fromWeek: amount.fromWeek,
          toWeek: amount.toWeek
        });
      });
    });

    const mappedSessions: Session[] = [];
    sessions.forEach((session: Session): void => {
      mappedSessions.push({
        ...session,
        startDate: new Date(session.startDate)
      });
    });

    const mappedCharityActivities: CharityTemplate[] = [];
    charityActivities.forEach((charityActivity: CharityActivity): void => {
      charityActivity.amounts.forEach((amount: Amount): void => {
        mappedCharityActivities.push({
          externalReference: amount.externalReference,
          name: charityActivity.name,
          dailyAmount: amount.amount,
          measurement: charityActivity.measurement,
          fromWeek: amount.fromWeek,
          toWeek: amount.toWeek
        });
      });
    });

    const templateToSave: AccountabilityTemplate = {
      measurementTemplate: weightings,
      conditions,
      measurement,
      recommendedExercise: mappedRecommendedExercises,
      optionalExercise: optionalExercises,
      selfCare: selfCareActivities,
      sessions: mappedSessions,
      programmeObjectReference: programmeExternalReference,
      charityTemplates: mappedCharityActivities
    };

    if (programme?.accountabilityExists) {
      updateAccountabilityTemplate(templateToSave, redirectUrl);
    } else {
      createAccountabilityTemplate(templateToSave, redirectUrl);
    }
  };

  const createAccountabilityTemplate = (accountabilityTemplate: AccountabilityTemplate, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to accountability template actions
      requestCreateAccountabilityTemplate({
        input: {
          accountabilityTemplate,
          programmeExternalReference
        },
        onSuccess: (): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          }
        }
      })
    );
  };

  const updateAccountabilityTemplate = (accountabilityTemplate: AccountabilityTemplate, redirectUrl?: string): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to accountability template actions
      requestUpdateAccountabilityTemplate({
        input: {
          accountabilityTemplate,
          programmeExternalReference
        },
        onSuccess: (): void => {
          if (redirectUrl) {
            navigate(redirectUrl);
          }
        }
      })
    );
  };

  const generateSessions = (sessionTemplate: SessionTemplate, onSuccess: () => void): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to accountability template actions
      requestGenerateAccountabilityTemplateSessions({
        input: sessionTemplate,
        onSuccess: (sessionTemplates: Session[]): void => {
          sessionTemplates = sessionTemplates?.map(
            (sessionTemplate: Session): Session => ({
              ...sessionTemplate,
              externalReference: generateUUID()
            })
          );
          if (sessionTemplates) {
            setSessions((prevState: Session[]): Session[] => [...prevState, ...sessionTemplates]);
          }
          onSuccess();
        }
      })
    );
  };

  const downloadLeaderboardExcel = (): void => {
    const leaderboardScores: MemberScore = leaderboard?.userScores;
    const programmeName: string = programme.name;
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to leaderboard actions
      requestDownloadLeaderboardExcel({
        input: {
          leaderboardScores,
          programmeName
        },
        onSuccess: (base64String: string): void =>
          downloadExcelFile(base64String, `${programme.name} - Leaderboard.xlsx`)
      })
    );
  };

  const downloadDocument = (resource: Resource): void => {
    const { documentExternalReference, name } = resource;

    dispatch(
      // @ts-expect-error TODO: Fix after adding types to document actions
      requestDownloadDocument({
        externalReference: documentExternalReference,
        onSuccess: (data: unknown): void => downloadFile(data, name)
      })
    );
  };

  const downloadTeamLeaderboardExcel = (): void => {
    dispatch(
      // @ts-expect-error TODO: Fix after adding types to programme actions
      requestDownloadTeamLeaderboardExcel({
        input: {
          programmeName: programme.name,
          teamScores: teamLeaderboard.teamScores
        }
      })
    );
  };

  return (
    <Accountability
      status={status as string}
      programme={programme}
      leaderboard={leaderboard}
      teamLeaderboard={teamLeaderboard}
      accountabilityTemplate={accountabilityTemplate}
      collections={collections}
      weightings={weightings}
      measurement={measurement}
      mealPlans={resources}
      conditions={conditions}
      recommendedExercises={recommendedExercises}
      optionalExercises={optionalExercises}
      charityActivities={charityActivities}
      selfCareActivities={selfCareActivities}
      sessions={sessions}
      updateWeighting={updateWeighting}
      updateMeasurement={updateMeasurement}
      createMealPlan={createMealPlan}
      updateMealPlan={updateMealPlan}
      deleteMealPlan={deleteMealPlan}
      createCondition={createCondition}
      updateCondition={updateCondition}
      deleteCondition={deleteCondition}
      createRecommendedExercise={createRecommendedExercise}
      updateRecommendedExercise={updateRecommendedExercise}
      deleteRecommendedExercise={deleteRecommendedExercise}
      createExerciseAmount={createExerciseAmount}
      updateExerciseAmount={updateExerciseAmount}
      deleteExerciseAmount={deleteExerciseAmount}
      createOptionalExercise={createOptionalExercise}
      updateOptionalExercise={updateOptionalExercise}
      deleteOptionalExercise={deleteOptionalExercise}
      createCharityActivity={createCharityActivity}
      updateCharityActivity={updateCharityActivity}
      deleteCharityActivity={deleteCharityActivity}
      createCharityAmount={createCharityAmount}
      updateCharityAmount={updateCharityAmount}
      deleteCharityAmount={deleteCharityAmount}
      createSelfCareActivity={createSelfCareActivity}
      updateSelfCareActivity={updateSelfCareActivity}
      deleteSelfCareActivity={deleteSelfCareActivity}
      updateSession={updateSession}
      deleteSession={deleteSession}
      saveAccountabilityTemplate={saveAccountabilityTemplate}
      generateSessions={generateSessions}
      downloadLeaderboardExcel={downloadLeaderboardExcel}
      downloadDocument={downloadDocument}
      downloadTeamLeaderboardExcel={downloadTeamLeaderboardExcel}
    />
  );
};

export default AccountabilityContainer;
